import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

// Define LocationPreference enum
const LocationPreference = {
  KIAWAH: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND'
};

// Define grouped area combinations
const AREA_GROUPS = {
  KIAWAH_SEABROOK: {
    value: 'KIAWAH_SEABROOK',
    label: 'Kiawah - Seabrook',
    areas: [LocationPreference.KIAWAH, LocationPreference.SEABROOK_ISLAND],
    isGroup: true
  },
  IOP_WILD_DUNES: {
    value: 'IOP_WILD_DUNES', 
    label: 'Isle of Palms & Isle of Palms - Wild Dunes',
    areas: [LocationPreference.ISLE_OF_PALMS, LocationPreference.ISLE_OF_PALMS_WILD_DUNES],
    isGroup: true
  }
};

// Available areas array with individual areas and grouped options
const AVAILABLE_AREAS = [
  // Grouped options at the top
  AREA_GROUPS.KIAWAH_SEABROOK,
  AREA_GROUPS.IOP_WILD_DUNES,
  // Divider
  { isDivider: true },
  // Individual areas
  { value: LocationPreference.KIAWAH, label: 'Kiawah', isGroup: false },
  { value: LocationPreference.ISLE_OF_PALMS, label: 'Isle of Palms', isGroup: false },
  { value: LocationPreference.ISLE_OF_PALMS_WILD_DUNES, label: 'Isle of Palms - Wild Dunes', isGroup: false },
  { value: LocationPreference.SEABROOK_ISLAND, label: 'Seabrook Island', isGroup: false }
];

export default function AreaFilter({ selectedAreas, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const dropdownRef = useRef(null);
  const { user, loading } = useAuth();
  
  // Initialize with user's areas if none are selected - but only once when auth is fully loaded
  useEffect(() => {
    // Don't initialize anything while auth is still loading
    if (loading) {
      return;
    }
    
    // Only auto-initialize once when the component first loads and auth is complete
    if (!hasInitialized) {
      if (user?.areas && Array.isArray(user.areas) && user.areas.length > 0) {
        // Only set user areas if none are currently selected
        if (selectedAreas.length === 0) {
          onChange(user.areas);
        }
      }
      
      // Mark as initialized after auth is complete (whether user has areas or not)
      setHasInitialized(true);
    }
  }, [loading, user, hasInitialized, onChange, selectedAreas.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper function to check if a group is fully selected
  const isGroupSelected = (group) => {
    return group.areas.every(area => selectedAreas.includes(area));
  };

  // Helper function to check if a group is partially selected
  const isGroupPartiallySelected = (group) => {
    return group.areas.some(area => selectedAreas.includes(area)) && !isGroupSelected(group);
  };

  const handleAreaToggle = (areaValue) => {
    // Check if this is a grouped selection
    const selectedGroup = Object.values(AREA_GROUPS).find(group => group.value === areaValue);
    
    if (selectedGroup) {
      // Handle group selection
      const isCurrentlySelected = isGroupSelected(selectedGroup);
      
      if (isCurrentlySelected) {
        // Deselect all areas in the group
        const newAreas = selectedAreas.filter(area => !selectedGroup.areas.includes(area));
        onChange(newAreas);
      } else {
        // Select all areas in the group (merge with existing selections)
        const newAreas = [...new Set([...selectedAreas, ...selectedGroup.areas])];
        onChange(newAreas);
      }
    } else {
      // Handle individual area selection
      if (selectedAreas.includes(areaValue)) {
        onChange(selectedAreas.filter(area => area !== areaValue));
      } else {
        onChange([...selectedAreas, areaValue]);
      }
    }
  };

  const handleClearAll = () => {
    onChange([]);
    setIsOpen(false);
  };

  const handleSelectAll = () => {
    // Select all individual areas
    const allIndividualAreas = AVAILABLE_AREAS
      .filter(area => !area.isGroup && !area.isDivider)
      .map(area => area.value);
    onChange(allIndividualAreas);
    setIsOpen(false);
  };

  // Get selected individual areas for display
  const selectedIndividualAreas = AVAILABLE_AREAS
    .filter(area => !area.isGroup && !area.isDivider && selectedAreas.includes(area.value));

  // Get selected groups for display
  const selectedGroups = Object.values(AREA_GROUPS).filter(group => isGroupSelected(group));

  return (
    <div className="relative" ref={dropdownRef}>
      <label htmlFor="areaFilter" className="block text-sm font-medium text-gray-700 mb-1">
        Areas
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <span className="flex items-center">
          {selectedAreas.length > 0 ? (
            <span className="flex items-center space-x-1">
              {/* Show selected groups first */}
              {selectedGroups.map(group => (
                <span key={group.value} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {group.label}
                </span>
              ))}
              {/* Show individual areas that aren't part of selected groups */}
              {selectedIndividualAreas
                .filter(area => !selectedGroups.some(group => group.areas.includes(area.value)))
                .slice(0, selectedGroups.length === 0 ? 1 : 0)
                .map(area => (
                  <span key={area.value} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {area.label}
                  </span>
                ))
              }
              {/* Show count if there are more selections */}
              {(selectedGroups.length + selectedIndividualAreas.filter(area => !selectedGroups.some(group => group.areas.includes(area.value))).length) > 1 && (
                <span className="text-gray-500">
                  +{selectedAreas.length - (selectedGroups.length > 0 ? selectedGroups.length * 2 : 1)}
                </span>
              )}
            </span>
          ) : (
            <span className="text-gray-500">All areas</span>
          )}
        </span>
        <svg
          className={`h-5 w-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div 
          className="fixed left-0 right-0 mt-1 mx-auto w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
          style={{ 
            zIndex: 9999,
            position: 'absolute',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Quick action buttons */}
          <div className="border-b border-gray-200 px-3 py-2">
            <div className="flex justify-between gap-2">
              <button
                type="button"
                onClick={handleClearAll}
                className="flex-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Clear All
              </button>
              <button
                type="button"
                onClick={handleSelectAll}
                className="flex-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
              >
                Select All
              </button>
            </div>
          </div>

          {/* Area options */}
          {AVAILABLE_AREAS.map((area, index) => {
            if (area.isDivider) {
              return (
                <div key={`divider-${index}`} className="border-t border-gray-200 my-1">
                  <div className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50">
                    Individual Areas
                  </div>
                </div>
              );
            }

            const isSelected = area.isGroup ? isGroupSelected(area) : selectedAreas.includes(area.value);
            const isPartiallySelected = area.isGroup ? isGroupPartiallySelected(area) : false;

            return (
              <div
                key={area.value}
                className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${
                  isSelected ? (area.isGroup ? 'bg-green-50' : 'bg-blue-50') : 
                  isPartiallySelected ? 'bg-yellow-50' : ''
                }`}
                onClick={() => handleAreaToggle(area.value)}
              >
                <div className="flex items-center">
                  <span className={`block truncate ${area.isGroup ? 'font-medium' : ''}`}>
                    {area.isGroup && '🔗 '}{area.label}
                    {area.isGroup && (
                      <span className="text-xs text-gray-500 font-normal ml-1">
                        ({area.areas.length} areas)
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                      area.isGroup ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                  {isPartiallySelected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-yellow-600">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      
      {/* Helper text */}
      <div className="mt-1">
        {selectedAreas.length === 0 ? (
          <p className="text-xs text-gray-500">Showing jobs from all areas</p>
        ) : (
          <p className="text-xs text-gray-500">
            {user?.areas && 
             user.areas.length > 0 && 
             selectedAreas.length === user.areas.length &&
             selectedAreas.every(area => user.areas.includes(area)) ? (
              <>Showing jobs from your assigned areas • <button 
                type="button" 
                onClick={handleClearAll}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                View all areas
              </button></>
            ) : (
              <>Filtered to {selectedAreas.length} area{selectedAreas.length !== 1 ? 's' : ''} • <button 
                type="button" 
                onClick={handleClearAll}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Clear filters
              </button></>
            )}
          </p>
        )}
      </div>
    </div>
  );
} 